import { Dialog, Transition } from '@headlessui/react'
import 'cropperjs/dist/cropper.css'
import { Dispatch, Fragment, SetStateAction, useEffect, useRef, useState } from 'react'
import { Cropper, ReactCropperElement } from 'react-cropper'
import { useTranslation } from 'react-i18next'
import { useMediaQuery, useOnClickOutside } from 'usehooks-ts'
import { Button, ButtonPrimary } from '../button'
import { Spinner } from '../common'
import { Cancel, Close, Save } from '../icons'
import './UploadImageModal.css'

export type UploadImageModalProps = {
  open: boolean
  title: string
  onClose: () => void
  handleLogo: (logo: File) => void
  imageName?: string | null
  defaultSrc?: string | null | undefined
  openSelectFile: () => void
  image: string | ArrayBuffer | null
  setImage: Dispatch<SetStateAction<string | ArrayBuffer | null>>
  error: string | null
}

export const UploadImageModal = ({
  title,
  open,
  onClose,
  handleLogo,
  defaultSrc,
  openSelectFile,
  image,
  imageName,
  setImage,
  error,
}: UploadImageModalProps) => {
  const { t } = useTranslation()

  const [cropper, setCropper] = useState<Cropper>()
  const [showSpinner, setShowSpinner] = useState<boolean>(false)

  useEffect(() => {
    if (defaultSrc) {
      setImage(defaultSrc)
    }
  }, [defaultSrc, setImage])

  const isMobile = useMediaQuery('(max-width: 767px)')

  const modalRef = useRef(null)
  const cropperRef = useRef<ReactCropperElement>(null)

  const onCloseModal = () => {
    onClose()
  }

  useOnClickOutside(modalRef, onCloseModal)

  const generateRandomString = (length: number) => {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    for (let i = 0; i < length; i += 1) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  }

  const onSave = async () => {
    if (cropper) {
      const blob = await fetch(cropper.getCroppedCanvas().toDataURL()).then((it) => it.blob())
      const newFile = new File([blob], `${generateRandomString(24)}.jpg`, {
        type: 'image/jpeg',
        lastModified: new Date().getTime(),
      })
      handleLogo(newFile)
      onCloseModal()
    }
  }

  return (
    <>
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className={'relative z-20'} open={open} onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto ">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  ref={modalRef}
                  as="div"
                  className={
                    'rounded-lg bg-white transform transition-all px-10 pt-10 pb-5 shadow-sm shadow-slate-400 relative flex flex-col gap-y-2 w-[632px] max-w-[632px]'
                  }
                >
                  <div
                    className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-4 top-4 cursor-pointer"
                    onClick={onCloseModal}
                  >
                    <Close />
                  </div>
                  <Dialog.Title
                    as="div"
                    className={'mb-4 text-xl font-medium text-navy-900 flex flex-row justify-between'}
                  >
                    <div className="font-bold">{title}</div>
                  </Dialog.Title>

                  <div className="flex flex-col gap-y-2 w-full">
                    <div className="flex flex-row gap-x-4 justify-start items-center text-slate-600">
                      <ButtonPrimary
                        title={t('common.chooseFile')}
                        className="outline-none w-fit"
                        onClick={openSelectFile}
                      />
                      {image && <div>{imageName}</div>}
                    </div>
                    {error && <div className="text-boldOrange text-sm w-fit">{error}</div>}

                    <div className="h-[300px] w-full">
                      <div className={`w-full h-full ${showSpinner ? 'hidden' : 'block'}`}>
                        {image ? (
                          <Cropper
                            ref={cropperRef}
                            style={{ height: '100%', width: '100%' }}
                            className="h-full w-full inline-block"
                            zoomTo={0}
                            aspectRatio={1}
                            preview=".img-preview"
                            src={image.toString()}
                            viewMode={1}
                            minCropBoxHeight={6}
                            minCropBoxWidth={6}
                            minContainerHeight={300}
                            minContainerWidth={isMobile ? 300 : 552}
                            background={false}
                            responsive
                            autoCropArea={1}
                            checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
                            onInitialized={(instance) => {
                              setShowSpinner(true)
                              setCropper(instance)
                            }}
                            ready={() => {
                              setShowSpinner(false)
                            }}
                            guides
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col justify-center ite1ms-center">
                            <div className="text-slate-600">{t('common.selectAnImage')}</div>
                          </div>
                        )}
                      </div>
                      <div
                        className={`w-full h-full items-center gap-y-1 justify-center ${
                          showSpinner ? 'flex flex-col' : 'hidden'
                        }`}
                      >
                        <Spinner />
                        <div className="text-sm text-slate-600">{t('common.loadingImage')}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row gap-x-4 justify-end mt-5">
                    <Button
                      className="bg-navy-900 px-4 font-semibold text-sm flex flex-row items-center gap-x-1 disabled:opacity-50 disabled:hover:bg-navy-900 outline-none text-white"
                      onClick={onCloseModal}
                    >
                      <Cancel />
                      {t('common.cancel')}
                    </Button>
                    <Button
                      disabled={!image}
                      className="bg-primary-500 py-2 px-4 text-white font-semibold text-sm flex flex-row items-center gap-x-1 disabled:opacity-50 disabled:hover:bg-primary-500 outline-none"
                      onClick={onSave}
                    >
                      <Save />
                      {t('common.save')}
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
